import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import useLink from "@qpr/client-api-base/hooks/useLink";
import useElement from "backend/hooks/elements/useElement";
import PerformanceTrend from "components/PerformanceTrend";
import ValueHistory from "components/ValueHistory";
import { PerformanceDonut } from "design/components/charts/PerformanceDonut";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import React from "react";
import Page from "design/pages/Page";
import {
  TargetAchievementCard,
  YearEndTargetCard,
} from "design/components/cards/KPIPerformanceCard";
import MeasureInfo from "design/components/cards/MeasureInfo";
import PageContentMenu from "@qpr/client-api-base/uicomponents/view/PageContentMenu";
import { t } from "i18next";
import Anchor from "@qpr/client-api-base/ui/Anchor";
import UploadedFilesList from "@qpr/client-api-base/ui/components/UploadedFilesList";

import useQueryBuilder from "@qpr/client-api-base/querybuilder/useQueryBuilder";
import ItemDrawer from "design/components/menus/ItemDrawer";
import defaultAttributes from "backend/defaultAttributes";
import { ICardElementWithChildren } from "backend/model/model";
import { useQuery } from "react-query";
import useYearsLastPeriod from "backend/hooks/model/useYearsLastPeriod";
import LeftMenuPosition from "design/layouts/LeftMenuPosition";
import InitiativeHeading from "design/components/texts/InitiativeHeading";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import { Series } from "design/components/charts/types";
import KPIToolbar from "components/toolbars/KPIToolbar";
import getValues from "backend/values";
import { resultSort } from "init/sort";
import useComments from "backend/hooks/comments/useComments";
import Todo from "design/components/misc/Todo";
import useAttachments from "backend/hooks/files/useAttachments";

const SERIES: Series[] = [
  { key: "TAR_YTD", name: "TARGET (YTD)", color: "rgb(93,98,104)" },
  {
    key: "ACT_YTD",
    name: "ACTUAL (YTD)",
    color: "rgba(38, 166, 242, 0.8)",
  },
];
const KPIDetails = () => {
  const ctx = useAppContext();
  const link = useLink();
  const { data: comments } = useComments({
    id: ctx.elementId || "",
    period: ctx.period || "",
  });
  const { data: attachments } = useAttachments({
    elementId: ctx.elementId,
    period: ctx.period,
  });
  const { data, isLoading } = useElement(ctx.elementId);
  const { data: scorecardKpis, isLoading: isScorecardKpisLoading } =
    useQueryBuilder<ICardElementWithChildren[]>((q) =>
      q(ctx.elementId)
        .scorecard()
        .attributes(...defaultAttributes(ctx.period, []))
        .measures({ criteria: { typename: "KPI" } })
    );

  const sortedScorecardKPIs = React.useMemo(
    () => resultSort(scorecardKpis?.data || []),
    [scorecardKpis]
  );

  const lastPeriod = useYearsLastPeriod(ctx.period || "");

  const { data: lastPeriodValue } = useQuery(
    `last-period-for-${ctx.elementId}-${ctx.period}-${lastPeriod?.name}`,
    async () => {
      return getValues(ctx.elementId || "", [lastPeriod!], true, []);
    },
    { enabled: !!lastPeriod }
  );

  const [firstComment] = comments || [];

  //           <ItemDrawer title="KPIs" items={scorecardKpis?.data!} />

  if (!data) {
    return null;
  }

  return (
    <Page
      isLoading={
        isLoading || isScorecardKpisLoading || !scorecardKpis?.data || !data
      }
      Toolbar={KPIToolbar}
    >
      <SplitView left="180px" right="1100px" gap="20px">
        <div>
          <LeftMenuPosition>
            <ItemDrawer
              rtl={ctx.rtl}
              title="KPIs"
              items={scorecardKpis?.data!}
              onSelect={(item) => {
                link({ meaId: item.id });
              }}
            />
            <PageContentMenu
              componentData={{
                items: [
                  { title: t("KPI Information"), anchor: "header" },
                  { title: t("Performance"), anchor: "performance" },
                  { title: t("Justification"), anchor: "justification" },
                  { title: t("Value data"), anchor: "valuedata" },
                ],
              }}
            />
          </LeftMenuPosition>
        </div>
        <div>
          <StackView>
            <Anchor name="header" />
            <MeasureInfo
              title="KPI Information"
              items={[
                {
                  label: "Description",
                  text: data!.description || "",
                },
                {
                  label: "Measurement Unit",
                  text: data!.ACT_unitname || "",
                },
                {
                  label: "Strategic Objective",
                  text:
                    (data?.parentTypename || "") === "Strategic Objective"
                      ? (data?.parentName || "") +
                        "\n" +
                        data?.parentDescription
                      : "",
                },
                {
                  label: "Measurement Frequency",
                  text: data!.periodlevelname || "",
                },
              ]}
            />

            <SplitView left="30%" right="70%" gap="30px">
              <div>
                <InitiativeHeading>Performance</InitiativeHeading>

                <TargetAchievementCard
                  title="Target Achievement YTD"
                  period={data.period?.name || ""}
                  data={data}
                  series={[
                    { title: "Actual (YTD)", series: "ACT_YTD_prettyvalue" },
                    { title: "Target (YTD)", series: "TAR_YTD_prettyvalue" },
                  ]}
                >
                  <PerformanceDonut
                    title="Score (YTD)"
                    element={data!}
                    valueKey="PERF_YTD"
                    prettyKey="PERF_YTD_prettyvalue"
                    colorKey="rangecolor_PERF_YTD_OPEN"
                  />
                </TargetAchievementCard>
                {lastPeriodValue && lastPeriodValue[0] ? (
                  <YearEndTargetCard
                    title="Year End Target"
                    period={lastPeriodValue[0]?.period?.name || ""}
                    value={lastPeriodValue[0].TAR_YTD_valuewithoutsymbol}
                    unit={lastPeriodValue[0].TAR_YTD_unit}
                  />
                ) : null}
              </div>
              <div>
                <Anchor name="performance" />
                <InitiativeHeading>Performance Trend</InitiativeHeading>
                <PerformanceTrend element={data!} series={SERIES} />
              </div>
            </SplitView>

            <Anchor name="justification" />
            <InitiativeHeading subTitle={ctx.period}>
              Actions and Recommendations
            </InitiativeHeading>

            {firstComment ? (
              <AnalysisCard
                userData={{
                  name: firstComment.modifiedby || firstComment.createdby,
                  subtitle: "Owner",
                }}
                date={new Date(
                  firstComment.lastmodifieddate
                ).toLocaleDateString()}
                description={
                  firstComment ? firstComment.description : "No Comments yet"
                }
              >
                <UploadedFilesList values={attachments} readOnly />
              </AnalysisCard>
            ) : (
              <AnalysisCard description="No Comments Yet">
                <UploadedFilesList values={attachments} readOnly />
              </AnalysisCard>
            )}

            <Anchor name="valuedata" />
            <InitiativeHeading>KPI Value Data</InitiativeHeading>
            <ValueHistory element={data!} />
            <div style={{ height: "500px" }} />
          </StackView>
        </div>
      </SplitView>
    </Page>
  );
};

export default KPIDetails;
